import { Component, OnInit, Output, Input, EventEmitter } from '@angular/core';
import { FilterValue } from 'src/shared/models/pipeline-config/metric-definition.models';
import { DataFieldDefinition, ReportingSetDefinition } from 'src/shared/models/pipeline-config/reporting-sets.models';
import { CxoneManageMetricsService } from '../cxone-manage-metrics.service';
import { cloneDeep } from 'lodash';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import moment from 'moment';
import { ComparisonType } from 'src/shared/models/pipeline-config/mongo-enums.model';

interface DataFieldComp extends DataFieldDefinition {
  name: string,
  label: string
}

interface ValuePair {
  name: any,
  value?: number
}

interface Operator {
  key: ComparisonType,
  value: string
}

interface Selections {
  Operator: Operator,
  DataField: DataFieldComp,
  DistinctDataValuesDDL: ValuePair[],
  OperatorList: Operator[],
  ValueList: ValuePair[],
  ValidationFlags: { Datafield: boolean, Operator: boolean; Value: boolean[] }
}


@Component({
  selector: 'cv-filter-fields-edit',
  templateUrl: './filter-fields-edit.component.html',
  styleUrls: ['./filter-fields-edit.component.scss']
})
export class FilterFieldsEditComponent implements OnInit {

  @Input() filterFields: FilterValue[];
  @Input() reportingSet: ReportingSetDefinition;
  @Input() metricDate: string;
  @Output() close = new EventEmitter();
  @Output() updateFilterFields = new EventEmitter();

  public newFilterFieldList: FilterValue[] = [];
  public origFilterFieldList: FilterValue[] = [];
  public dataValuesList: string[] = [];
  public filteredDataList: string[] = [];
  public isLoading: boolean;
  public DataFieldList: DataFieldComp[] = [];
  public trueText: string;
  public falseText: string;
  public OperatorList: Operator[] = [];
  public selectionsList: Selections[] = [];
  public origSelectionsList: Selections[] = [];
  public IsValid: boolean = false;

  constructor(
    private manageMetricsService: CxoneManageMetricsService,
    private cvLocalizationService: CVLocalizationService
  ) { }

  async ngOnInit(): Promise<void> {

    let emptyFilter: Selections = {
      DataField: null,
      Operator: null,
      OperatorList: [],
      ValueList: [],
      DistinctDataValuesDDL: [],
      ValidationFlags: { Datafield: false, Operator: false, Value: [] }
    }
    this.isLoading = true;

    //Assigning using this method to prevent them from modifying each other. 
    this.newFilterFieldList = JSON.parse(JSON.stringify(this.filterFields));
    this.newFilterFieldList = this.newFilterFieldList.reverse();
    this.origFilterFieldList = JSON.parse(JSON.stringify(this.newFilterFieldList));
    let dataFieldList: string[] = [];

    //Filter in only the string Data Field types
    for (let key in this.reportingSet.DataFields) {
      if (this.reportingSet.DataFields[key].ExpectedType == 0) {
        dataFieldList.push(key);
      }
    }

    this.filteredDataList = await this.manageMetricsService.getDistinctValuedDictForDataField(this.reportingSet.ReportingSetName, dataFieldList, this.metricDate, 10);

    this.loadDataFields();
    this.loadSelections();
    this.loadValueList();

    this.trueText = this.getLocalizationString("True", "True");
    this.falseText = this.getLocalizationString("False", "False");

    this.isLoading = false;

    if (this.newFilterFieldList.length == 0) {
      this.addFilterField();
    }

    if (this.selectionsList.length > 0 && this.selectionsList[0].Operator) {
      this.selectionsList=[emptyFilter,...this.selectionsList.reverse()]
    }

    this.origSelectionsList = cloneDeep(this.selectionsList);
  }

  public CloseModal() {
    this.close.emit();
  }

  public addFilterField() {
    let emptyFilter: Selections = {
        DataField: null,
        Operator: null,
        OperatorList: [],
        ValueList: [],
        DistinctDataValuesDDL: [],
        ValidationFlags: { Datafield: false, Operator: false, Value: [] }
    }

    if (this.selectionsList.length === 0) {
        this.selectionsList.unshift(emptyFilter);
    } else { 
        const firstElement = this.selectionsList.shift(); 
        this.selectionsList.push(firstElement!); 
        this.selectionsList.unshift(emptyFilter)
    }

    this.Validate();
}

  public AddClick() {
    //This will turn the boolean strings back into regular boolean values so that they can be saved to the backend properly
    if(this.checkNewFilter()) {
      this.addFilterField()
    } 
    
    this.selectionsList.slice(1).forEach((item) => {
      if (item.DataField.ExpectedType == 4) {
        item.ValueList.forEach((val, ci) => {
          item.ValueList[ci].name = item.ValueList[ci].name == 'true';
        });
      }
    });

    this.loadSelectionsIntoFilterFields();
    this.updateFilterFields.emit(this.newFilterFieldList);
  }

  public onTrashClick(index: number) {
    if (this.selectionsList.length > 1) {
      this.selectionsList.splice(index, 1);
      this.Validate();
    }
  }

  public changesMade(): boolean {
    return JSON.stringify(this.origSelectionsList) != JSON.stringify(this.selectionsList);
  }

  public areFilterFieldsValid(): boolean {
    return this.IsValid && !this.isLoading && this.changesMade();
  }
  public getLocalizationStringUpper(key: string, defaultValue: string) {
    return this.cvLocalizationService.getCachedText("CXOne_Manage_Metrics", key, defaultValue).toUpperCase();
  }

  public getLocalizationString(key: string, defaultValue: string) {
    return this.cvLocalizationService.getCachedText("CXOne_Manage_Metrics", key, defaultValue);
  }

  public getFilterHeading(index: number): string {
    let filterStr = this.cvLocalizationService.getCachedText("CXOne_Manage_Metrics", "Filter", "Filter");

    if(index === 0) {
      return this.cvLocalizationService.getCachedText("CXOne_Manage_Metrics", "New_Filter");
    }


    return `${filterStr} ${index}`;
  }


  public deleteValue(FFindex: number, compIndex: number) {
    if (this.selectionsList[FFindex].ValueList.length != 1) {
      this.selectionsList[FFindex].ValueList.splice(compIndex, 1);
    }
    this.selectionsList[FFindex].ValidationFlags.Value.splice(compIndex, 1);

    this.Validate();
  }

  public addValue(FFindex: number) {
    let expectedType = this.getTypeString(this.reportingSet.DataFields[this.selectionsList[FFindex].DataField.name].ExpectedType);
    if (expectedType == "Boolean") {
      this.selectionsList[FFindex].ValueList.push({ name: 'true' });
    } else if (expectedType == "String") {
      this.selectionsList[FFindex].ValueList.push(null);
    } else if (expectedType == "Integer" || expectedType == "Floating-Point") {
      this.selectionsList[FFindex].ValueList.push({ name: 0 });
    } else if (expectedType == "Date") {
      let now = moment().seconds(0);
      this.selectionsList[FFindex].ValueList.push({ name: now.toDate() });
    }

    this.selectionsList[FFindex].ValidationFlags.Value.push(true);
    this.Validate();
  }

  public loadDataFields() {
    this.DataFieldList = Object.keys(this.reportingSet.DataFields).map((index) => {
      return {
        Field: this.reportingSet.DataFields[index].Field,
        ExpectedType: this.reportingSet.DataFields[index].ExpectedType,
        JoinID: this.reportingSet.DataFields[index].JoinID,
        name: index,
        label: `${index} (${this.getTypeString(this.reportingSet.DataFields[index].ExpectedType)})`,
      }
    });
  }

  public getTypeString(ExpectedType: number) {

    switch (ExpectedType) {
      case 0:
        return "String";
      case 1:
        return "Integer";
      case 2:
        return "Floating-Point";
      case 3:
        return "Date";
      case 4:
        return "Boolean"
      default:
        return "";
    }

  }

  public onSelOpened(templateRef: any) {
    //adding this 0ms wait so if another box closes due to the open click it will fire that close event first.
    setTimeout(() => {
      let $dropdown = $(templateRef.dropdownElRef.nativeElement).parent();

      let $modalbody = $dropdown.closest(".modal-body");
      $modalbody.css("overflow", "hidden");

      let toppos = $dropdown.position().top;
      $dropdown.css("position", "absolute").css("top", toppos);
    }, 0);
  }

  public onSelClosed(templateRef: any) {
    let $dropdown = $(templateRef.dropdownElRef.nativeElement).parent();

    let $modalbody = $dropdown.closest(".modal-body");

    $modalbody.css("overflow", "");
    $dropdown.css("position", "").css("top", "");
  }


  public dataFieldChange(e, FFIndex: number) {
    this.selectionsList[FFIndex].DataField = e;
    this.selectionsList[FFIndex].Operator = null;
    this.selectionsList[FFIndex].OperatorList = [];
    this.selectionsList[FFIndex].ValueList = [];
    this.selectionsList[FFIndex].ValidationFlags.Value = [];
    this.selectionsList[FFIndex].DistinctDataValuesDDL = [];
    this.selectionsList[FFIndex].ValidationFlags.Datafield = true;
    this.selectionsList[FFIndex].ValidationFlags.Operator = true;

    this.filteredDataList[this.selectionsList[FFIndex].DataField.name]?.forEach((item) => {
      this.selectionsList[FFIndex].DistinctDataValuesDDL.push({ name: item });
    });

    let localMap: Map<string, string> = new Map([
      ["Contains", this.getLocalizationString("Contains", "contains")],
      ["EndWith", this.getLocalizationString("EndWith", "ends with")],
      ["StartWith", this.getLocalizationString("StartWith", "starts with")]
    ]);

    let tempList: [ComparisonType, string][];
    switch (this.selectionsList[FFIndex].DataField.ExpectedType) {
      case 0:
        tempList = this.manageMetricsService.getStringComparisonTypes(localMap);
        break;
      case 1:
      case 2:
      case 3:
        tempList = this.manageMetricsService.numericComparisonTypes;
        break;
      case 4:
      default:
        tempList = [[ComparisonType.Equal, "="]];
    }

    this.selectionsList[FFIndex].OperatorList = tempList.map(item => {
      return { key: item[0], value: item[1] }
    });

    this.addValue(FFIndex);
    this.Validate();
  }

  public operatorChange(e, FFIndex: number) {
    this.selectionsList[FFIndex].Operator = e;
    this.Validate();
  }

  getComparisonType(value: number) {
    switch (value) {
      case 0:
        return ComparisonType.Equal;
      case 1:
        return ComparisonType.NotEqual;
      case 2:
        return ComparisonType.GreaterThan;
      case 3:
        return ComparisonType.GreaterThanOrEqual;
      case 4:
        return ComparisonType.LessThan;
      case 5:
        return ComparisonType.LessThanOrEqual;
      case 6:
        return ComparisonType.Contain;
      case 7:
        return ComparisonType.StartWith;
      case 8:
        return ComparisonType.EndWith;
    }

  }

  public addTextItem(e: any, compindex: number, FFIndex: number) {
    if (e.text != "" && e.text != undefined && e.text != null) {
      let newValue = { name: e.text };
      this.selectionsList[FFIndex].DistinctDataValuesDDL = this.selectionsList[FFIndex].DistinctDataValuesDDL.concat(newValue);
      this.selectionsList[FFIndex].ValueList[compindex] = newValue;
    }

    this.Validate();
  }

  public trackByIndex(index: number, item: any) {
    return index;
  }

  public loadSelections() {
    this.newFilterFieldList.forEach((filterField, idx) => {
      this.selectionsList[idx] = {
        Operator: null,
        DataField: this.DataFieldList.find(item => item.name == filterField.EvalField),
        DistinctDataValuesDDL: [],
        OperatorList: [],
        ValueList: [],
        ValidationFlags: { Datafield: false, Operator: false, Value: [] }
      };

      this.filteredDataList[this.newFilterFieldList[idx].EvalField]?.forEach((item) => {
        this.selectionsList[idx].DistinctDataValuesDDL.push({ name: item });
      });

      let localMap: Map<string, string> = new Map([
        ["Contains", this.getLocalizationString("Contains", "contains")],
        ["EndWith", this.getLocalizationString("EndWith", "ends with")],
        ["StartWith", this.getLocalizationString("StartWith", "starts with")]
      ]);

      let tempList: [ComparisonType, string][];
      switch (this.selectionsList[idx].DataField.ExpectedType) {
        case 0:
          tempList = this.manageMetricsService.getStringComparisonTypes(localMap);
          break;
        case 1:
        case 2:
        case 3:
          tempList = this.manageMetricsService.numericComparisonTypes;
          break;
        case 4:
        default:
          tempList = [[ComparisonType.Equal, "="]];
      }

      this.selectionsList[idx].OperatorList = tempList.map(item => {
        return { key: item[0], value: item[1] }
      });

      this.selectionsList[idx].Operator = this.selectionsList[idx].OperatorList.find(item => item.key == this.newFilterFieldList[idx].ComparisonType);

    });
  }

  public loadValueList() {

    this.newFilterFieldList.forEach((filterField, idx) => {

      filterField.CompValues.forEach(item => { this.selectionsList[idx].ValueList.push({ name: item }) });
      this.selectionsList[idx].ValueList = this.selectionsList[idx].ValueList.filter(item => item != null && item != undefined);

      if (this.selectionsList[idx].DataField.ExpectedType == 3) {
        for (let i = 0; i < this.selectionsList[idx].ValueList.length; i++) {
          this.selectionsList[idx].ValueList[i].name = new Date(this.selectionsList[idx].ValueList[i].name);
        }
      }

      //turn the boolean values into strings so that they'll work with the CXOne Single-Select Dropdowns (regular booleans don't work for some reason)
      if (this.selectionsList[idx].DataField.ExpectedType == 4) {
        for (let i = 0; i < this.selectionsList[idx].ValueList.length; i++) {
          this.selectionsList[idx].ValueList[i].name = this.selectionsList[idx].ValueList[i].name == true ? 'true' : "false";
        }
      }

      if (this.selectionsList[idx].ValueList.length > 0) {
        for (let i = 0; i < this.selectionsList[idx].ValueList.length; i++) {
          if (this.selectionsList[idx].ValueList[i].name == undefined) {
            this.selectionsList[idx].ValueList[i] = null;
          }
        }
      }

      this.selectionsList[idx].ValidationFlags.Value.push(true);
    });

    this.Validate();
  }

  public Validate() {
    this.resetValidationFlags();
    this.selectionsList.forEach((item, index) => {
      if (item.DataField == null || item.DataField == undefined) {
        this.selectionsList[index].ValidationFlags.Datafield = false;
      }

      if (item.Operator == null || item.Operator == undefined) {
        this.selectionsList[index].ValidationFlags.Operator = false;
      }

      item.ValueList.forEach((val, idx) => {

        if (item.DataField.ExpectedType == 0) {
          //Validate Strings types
          this.selectionsList[index].ValidationFlags.Value[idx] = !(val?.name == null || val.name == "");
        }

        if (item.DataField.ExpectedType == 1) {
          //Validate Int types (make sure they are not decimals)
          this.selectionsList[index].ValidationFlags.Value[idx] = this.IsInt(val.name);
        }

      })

      this.CheckIfValid();
    });
  }

  public resetValidationFlags() {
    this.selectionsList.forEach(item => {
      item.ValidationFlags.Datafield = true;
      item.ValidationFlags.Operator = true;
      item.ValidationFlags.Value.forEach(item => { item = true });
    });
  }

  public CheckIfValid() {
    if (this.selectionsList.length === 1) {
      this.IsValid = this.checkNewFilter(); 
  } else {
      this.IsValid = this.selectionsList.slice(1).every(selection => {
          return selection.ValidationFlags.Datafield && 
                 selection.ValidationFlags.Operator && 
                 selection.ValidationFlags.Value.every(val => val === true);
      });
  }
}


  public checkNewFilter() {
    return  this.selectionsList[0]?.ValidationFlags.Datafield  && 
            this.selectionsList[0]?.ValidationFlags.Operator && 
            this.selectionsList[0]?.ValidationFlags.Value.every(val => val === true);  
  }



  public IsInt(input: number) {
    return input % 1 == 0;
  }

  public onValueChange(e, FFIndex: number, compIndex: number) {

    if (this.selectionsList[FFIndex].DataField.ExpectedType == 0) {
      this.selectionsList[FFIndex].ValueList[compIndex] = e;
    }
    else {
      this.selectionsList[FFIndex].ValueList[compIndex].name = e;
    }

    this.Validate();
  }

  public onOperatorChange(e, FFIndex: number) {
    this.selectionsList[FFIndex].Operator = e;
    this.Validate();
  }

  public loadSelectionsIntoFilterFields() {
    this.newFilterFieldList = [];
    this.selectionsList.slice(1).forEach((item, index) => {
      let filterField: FilterValue = {
        EvalField: item.DataField.name,
        ComparisonType: item.Operator.key,
        CompValues: item.ValueList.map(val => val.name ?? val),
        Negated: false,
        IsDate: item.DataField.ExpectedType == 3
      }

      //Assigning them by index rather than pushing to ensure the indexes match up perfectly with the newFilterFieldList
      this.newFilterFieldList[index] = filterField;
    });
  }

  public cancelClick() {
    this.selectionsList = cloneDeep(this.origSelectionsList);
    this.CloseModal();
  }

}
